import React from 'react';
import PropTypes from 'prop-types';

/**
 * Inline SVG icons for the email-tracking timeline steps and status badges.
 * `currentColor` is used throughout so the consuming CSS controls the colour.
 * Ported from the client app so both timelines look identical.
 */

export const SentIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="m22 2-7 20-4-9-9-4 20-7Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

export const DeliveredIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="m3 7 9 6 9-6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

export const OpenedIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M3 9.5 12 3l9 6.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9.5Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="m3 9.5 9 6 9-6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

/** Invoice-progress step: salary created (document with a plus). */
export const SalaryCreatedIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M12 12v6m-3-3h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/** Invoice-progress step: paid (check inside a circle). */
export const PaidIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path
      d="m8.5 12 2.5 2.5 4.5-5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const BouncedIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const FailedIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path d="m15 9-6 6m0-6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const NotSentIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="m3 7 9 6 9-6" stroke="currentColor" strokeWidth="2" />
  </svg>
);

export const CloseIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const ChevronDownIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ChevronUpIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="m6 15 6-6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** The row-action trigger in the invoice table (a pulse/tracking line). */
export const TrackingIcon = ({ size = 24, color = '#7C3AED' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3 12h4l3 8 4-16 3 8h4"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Icon for a given email status. */
export const EmailStatusIcon = ({ status, size }) => {
  switch (status) {
    case 'opened':
      return <OpenedIcon size={size} />;
    case 'delivered':
      return <DeliveredIcon size={size} />;
    case 'sent':
      return <SentIcon size={size} />;
    case 'bounced':
      return <BouncedIcon size={size} />;
    case 'failed':
      return <FailedIcon size={size} />;
    case 'not_sent':
    default:
      return <NotSentIcon size={size} />;
  }
};

/** Icon for the tracking-modal header pill (email statuses + invoice stages). */
export const TimelineStatusIcon = ({ status, size }) => {
  switch (status) {
    case 'paid':
    case 'salary_paid':
      return <PaidIcon size={size} />;
    case 'salary_created':
      return <SalaryCreatedIcon size={size} />;
    default:
      return <EmailStatusIcon status={status} size={size} />;
  }
};

/** Icon for a given timeline step node. */
export const StepIcon = ({ type, size = 20 }) => {
  switch (type) {
    case 'sent':
      return <SentIcon size={size} />;
    case 'delivered':
      return <DeliveredIcon size={size} />;
    case 'opened':
      return <OpenedIcon size={size} />;
    case 'paid':
    case 'salaryPaid':
      return <PaidIcon size={size} />;
    case 'salaryCreated':
      return <SalaryCreatedIcon size={size} />;
    default:
      return <SentIcon size={size} />;
  }
};

const sizeProp = { size: PropTypes.number };

SentIcon.propTypes = sizeProp;
DeliveredIcon.propTypes = sizeProp;
OpenedIcon.propTypes = sizeProp;
SalaryCreatedIcon.propTypes = sizeProp;
PaidIcon.propTypes = sizeProp;
BouncedIcon.propTypes = sizeProp;
FailedIcon.propTypes = sizeProp;
NotSentIcon.propTypes = sizeProp;
CloseIcon.propTypes = sizeProp;
ChevronDownIcon.propTypes = sizeProp;
ChevronUpIcon.propTypes = sizeProp;
TrackingIcon.propTypes = { size: PropTypes.number, color: PropTypes.string };
EmailStatusIcon.propTypes = { status: PropTypes.string, size: PropTypes.number };
TimelineStatusIcon.propTypes = { status: PropTypes.string, size: PropTypes.number };
StepIcon.propTypes = { type: PropTypes.string, size: PropTypes.number };
